import {
  Button,
  Icon,
  ListItem,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { UserGroupIcon, UserIcon } from '@heroicons/react/solid';
import { useTranslations } from 'use-intl';

import { FundraisingType } from '@blockpulse3/data/shared';
import { IconBoxWrapper, ResponsiveModal, ResponsiveModalProps } from '@blockpulse3/ui/commons';

type Props = {
  isOpen: boolean;
  onClick: (fundraisingType: FundraisingType) => void;
} & Omit<ResponsiveModalProps, 'children'>;

export function FundraisingTypeModal({ onClick, ...props }: Props): JSX.Element {
  const i18nFundraisingTypeModal = useTranslations('FundraisingTypeModal');
  const i18nFundraisingPrivate = useTranslations('FundraisingTypeModal.FundraisingPrivate');
  const i18nFundraisingCrowdfunding = useTranslations(
    'FundraisingTypeModal.FundraisingCrowdfunding',
  );

  const handleNewFundraisingClick = (type: FundraisingType): void => {
    onClick(type);
  };

  return (
    <ResponsiveModal isCentered {...props}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="lg">{i18nFundraisingTypeModal('Title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="5" w="full">
            <Button
              data-cy="create-private-fundraising"
              pl="24px"
              variant="container"
              onClick={(): void => handleNewFundraisingClick(FundraisingType.PRIVATE)}
            >
              <IconBoxWrapper mr="24px">
                <Icon as={UserIcon} boxSize="20px" color="gray.700" />
              </IconBoxWrapper>
              <Stack>
                <Text>{i18nFundraisingPrivate('Title')}</Text>
                <UnorderedList color="gray.700" fontSize="sm" fontWeight="light" pl="1.5">
                  <ListItem>{i18nFundraisingPrivate('KnowSubscribers')}</ListItem>
                  <ListItem>{i18nFundraisingPrivate('KnowTheirSubscriptionAmounts')}</ListItem>
                  <ListItem>{i18nFundraisingPrivate('LessSubscribers')}</ListItem>
                </UnorderedList>
              </Stack>
            </Button>
            <Button
              data-cy="create-crowdfunding-fundraising"
              pl="24px"
              variant="container"
              onClick={(): void => handleNewFundraisingClick(FundraisingType.CROWDFUNDING)}
            >
              <IconBoxWrapper mr="24px">
                <Icon as={UserGroupIcon} boxSize="20px" color="gray.700" />
              </IconBoxWrapper>
              <Stack>
                <Text>{i18nFundraisingCrowdfunding('Title')}</Text>
                <UnorderedList color="gray.700" fontSize="sm" fontWeight="light" pl="1.5">
                  <ListItem>{i18nFundraisingCrowdfunding('MoreSubscribers')}</ListItem>
                  <ListItem>{i18nFundraisingCrowdfunding('UnknownAmountsAndSubscribers')}</ListItem>
                </UnorderedList>
              </Stack>
            </Button>
          </Stack>
        </ModalBody>
      </ModalContent>
    </ResponsiveModal>
  );
}
